<template>
  <el-drawer
    v-model="visible"
    direction="btt"
    size="100%"
    :with-header="false"
    class="promo-detail-drawer"
  >
    <div v-if="promotion" class="pd-layout">
      <!-- Scrollable Content -->
      <div class="pd-content-scroll">
        
        <!-- Hero Header -->
        <div class="pd-hero-container">
          <div 
            v-if="promotion.imageUrl" 
            class="pd-hero-img" 
            :style="{ backgroundImage: `url(${getApiUrl(promotion.imageUrl)})` }"
          ></div>
          <div v-else class="pd-hero-placeholder">
            <el-icon :size="48"><Picture /></el-icon>
          </div>
          
          <!-- Top nav buttons floating -->
          <div class="pd-top-nav">
            <button class="pd-nav-btn" @click="visible = false"><el-icon :size="20"><ArrowLeft /></el-icon></button>
            <button class="pd-nav-btn" @click="visible = false"><el-icon :size="20"><Close /></el-icon></button>
          </div>
          
          <!-- Recommended Tag -->
          <div class="pd-hero-tag" v-if="promotion.isRecommended">
             <el-icon class="mr-1"><MagicStick /></el-icon> โปรโมชั่นแนะนำ
          </div>
        </div>

        <div class="pd-body-card">
          <!-- Title & Subtitle -->
          <h2 class="pd-title">{{ promotion.title }}</h2>
          <p class="pd-subtitle">{{ promotion.description }}</p>
          
          <!-- Features Row -->
          <div class="pd-features-row" v-if="promotion.features && promotion.features.filter(Boolean).length > 0">
             <div class="pd-feature" v-for="(feat, idx) in promotion.features.filter(Boolean)" :key="idx">
                <div class="pd-feature-icon">
                  <el-icon v-if="idx === 0"><CircleCheck /></el-icon>
                  <el-icon v-else-if="idx === 1"><User /></el-icon>
                  <el-icon v-else><FirstAidKit /></el-icon>
                </div>
                <span>{{ feat }}</span>
             </div>
          </div>

          <!-- Price Card -->
          <div class="pd-price-card" v-if="promotion.priceSale != null">
             <div class="pd-price-row">
                <div class="pd-price-left">
                   <div class="pd-old-price" v-if="packageInfo?.price != null && packageInfo.price !== promotion.priceSale">
                      ฿ {{ packageInfo.price.toLocaleString() }}
                   </div>
                   <div class="pd-price-badge">พิเศษเพียง</div>
                </div>
                <div class="pd-price-main">
                   <span class="pd-currency">฿</span> {{ promotion.priceSale.toLocaleString() }}
                </div>
                <div class="pd-price-save" v-if="packageInfo?.price != null && packageInfo.price > promotion.priceSale">
                   <div class="save-icon">฿</div>
                   <div class="save-text">ประหยัดไป<br/>฿ {{ (packageInfo.price - promotion.priceSale).toLocaleString() }}</div>
                </div>
             </div>
             <div class="pd-end-date" v-if="promotion.endDate">
                <el-icon><Calendar /></el-icon> ลดถึง {{ formatDate(promotion.endDate) }}
             </div>
          </div>

          <!-- Accordion Package Details -->
          <div class="pd-section-card" v-if="packageInfo">
            <div class="pd-section-header" @click="isPkgExpanded = !isPkgExpanded">
               <div class="pd-section-icon">
                 <el-icon :size="20">
                   <Odometer v-if="packageInfo.serviceType === 'CHECKUP'" />
                   <Grape v-else-if="packageInfo.serviceType === 'DENTAL'" />
                   <FirstAidKit v-else />
                 </el-icon>
               </div>
               <h3 class="pd-section-title">รายละเอียดแพ็กเกจ</h3>
               <el-icon class="pd-chevron" :class="{'is-up': isPkgExpanded}"><ArrowDown /></el-icon>
            </div>
            <el-collapse-transition>
              <div v-show="isPkgExpanded" class="pd-section-content">
                <div v-if="displayExamItems && displayExamItems.length > 0" class="pd-exam-list">
                  <div class="pd-exam-item" v-for="(item, idx) in displayExamItems" :key="idx">
                    <el-icon class="pd-check-icon"><Check /></el-icon>
                    <span>{{ item }}</span>
                  </div>
                </div>
                <div v-else-if="packageInfo.detailHtml" class="pd-package-html" v-html="packageInfo.detailHtml"></div>
                <div v-else class="pd-empty-state">ไม่มีรายละเอียดแพ็กเกจ</div>
              </div>
            </el-collapse-transition>
          </div>

          <!-- Suitable For -->
          <div class="pd-info-block" v-if="displaySuitableFor && displaySuitableFor.length > 0">
             <div class="pd-info-head">
                <el-icon color="#3b82f6" :size="20"><UserFilled /></el-icon>
                <h4>เหมาะสำหรับ</h4>
             </div>
             <div class="pd-info-tags">
                <span class="info-tag" v-for="tag in displaySuitableFor" :key="tag">{{ tag }}</span>
             </div>
          </div>

          <!-- Additional Info -->
          <div class="pd-info-block" v-if="displayAdditionalInfo">
             <div class="pd-info-head">
                <el-icon color="#3b82f6" :size="20"><Document /></el-icon>
                <h4>ข้อมูลเพิ่มเติม</h4>
             </div>
             <div class="pd-info-box">
                <p>{{ displayAdditionalInfo }}</p>
             </div>
          </div>
          
          <div class="pd-bottom-spacer"></div>

        </div>
      </div>

      <!-- Fixed Footer CTA -->
      <div class="pd-footer">
        <el-button 
          v-if="packageInfo" 
          type="primary" 
          class="pd-btn-book" 
          @click="onBookItem"
        >
          <el-icon class="mr-2" :size="18"><Calendar /></el-icon> นัดหมายแพ็กเกจนี้
        </el-button>
        <el-button 
          v-else 
          type="primary" 
          class="pd-btn-book" 
          @click="visible = false"
        >
          ปิดหน้าต่าง
        </el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { 
  Close, Picture, ArrowLeft, ArrowDown,
  MagicStick, CircleCheck, User, FirstAidKit, Odometer, Grape,
  UserFilled, Document, Calendar
} from '@element-plus/icons-vue';

const props = defineProps<{
  modelValue: boolean;
  promotion: any;
  packageInfo: any;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'book', packageInfo: any): void;
}>();

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const isPkgExpanded = ref(true);

const displayExamItems = computed(() => {
  if (props.packageInfo && props.packageInfo.examItems && props.packageInfo.examItems.length > 0) {
    return props.packageInfo.examItems;
  }
  return props.promotion?.examItems || [];
});

const displaySuitableFor = computed(() => {
  if (props.packageInfo && props.packageInfo.suitableFor && props.packageInfo.suitableFor.length > 0) {
    return props.packageInfo.suitableFor;
  }
  return props.promotion?.suitableFor || [];
});

const displayAdditionalInfo = computed(() => {
  if (props.packageInfo && props.packageInfo.additionalInfo) {
    return props.packageInfo.additionalInfo;
  }
  return props.promotion?.additionalInfo || "";
});

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function getApiUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith("http")) return path;
  const baseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || "/api/v1";
  const base = baseUrl.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function onBookItem() {
  if (props.packageInfo) {
    emit('book', props.packageInfo);
  }
}
</script>

<style scoped>
/* Layout */
.pd-layout {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
}
.pd-content-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  background-color: #e2e8f0; /* Underneath the hero image */
}

/* Hero Section */
.pd-hero-container {
  width: 100%;
  height: 320px;
  position: relative;
  background: #e2e8f0;
}
.pd-hero-img {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}
.pd-hero-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
}

/* Floating Navigation */
.pd-top-nav {
  position: absolute;
  top: 24px;
  left: 20px;
  right: 20px;
  display: flex;
  justify-content: space-between;
  z-index: 10;
}
.pd-nav-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0f172a;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

/* Floating Tag */
.pd-hero-tag {
  position: absolute;
  bottom: 48px;
  left: 24px;
  background: #f43f5e;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(244, 63, 94, 0.3);
  z-index: 10;
}

/* Main White Card */
.pd-body-card {
  background: white;
  border-radius: 32px 32px 0 0;
  margin-top: -32px;
  padding: 32px 24px 24px;
  position: relative;
  z-index: 2;
  min-height: 50vh;
}

/* Text */
.pd-title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
}
.pd-subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
}

/* Features */
.pd-features-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 24px 0;
}
.pd-feature {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}
.pd-feature-icon {
  width: 36px;
  height: 36px;
  background: #eff6ff;
  color: #3b82f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pd-feature-icon .el-icon {
  font-size: 18px;
}

/* Price Box */
.pd-price-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  margin-bottom: 32px;
}
.pd-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.pd-price-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.pd-old-price {
  color: #94a3b8;
  text-decoration: line-through;
  font-size: 16px;
  font-weight: 600;
}
.pd-price-badge {
  background: #f43f5e;
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.pd-price-main {
  color: #f43f5e;
  font-size: 32px;
  font-weight: 800;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.pd-currency {
  font-size: 18px;
  font-weight: 700;
}
.pd-price-save {
  background: #ffe4e6;
  color: #e11d48;
  padding: 6px 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.save-icon {
  width: 18px;
  height: 18px;
  border: 1px solid #e11d48;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
}
.save-text {
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
}
.pd-end-date {
  font-size: 13px;
  color: #64748b;
  margin-top: 12px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Accordion */
.pd-section-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 32px;
  background: white;
}
.pd-section-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
}
.pd-section-icon {
  width: 36px;
  height: 36px;
  background: #eff6ff;
  color: #3b82f6;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}
.pd-section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
}
.pd-chevron {
  color: #64748b;
  transition: transform 0.3s;
}
.pd-chevron.is-up {
  transform: rotate(180deg);
}
.pd-section-content {
  padding: 0 20px 20px;
}
.pd-package-html {
  font-size: 13px;
  color: #3b82f6;
}
.pd-package-html :deep(ul) {
  list-style: none;
  padding: 0;
  margin: 0;
}
.pd-package-html :deep(li) {
  position: relative;
  padding-left: 32px;
  margin-bottom: 14px;
  line-height: 1.5;
  color: #475569;
}
.pd-package-html :deep(li)::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0;
  width: 20px;
  height: 20px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}
.pd-exam-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pd-exam-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #475569;
  font-size: 13px;
  line-height: 1.5;
}
.pd-check-icon {
  width: 20px;
  height: 20px;
  background: #3b82f6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}
.pd-empty-state {
  color: #94a3b8;
  font-size: 13px;
}

/* Info Blocks */
.pd-info-block {
  margin-bottom: 24px;
}
.pd-info-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.pd-info-head h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}
.pd-info-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.info-tag {
  background: #f1f5f9;
  color: #334155;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.pd-info-box {
  background: #f8fafc;
  padding: 16px;
  border-radius: 16px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}
.pd-info-box p {
  margin: 0 0 12px;
}
.pd-info-box-footer {
  display: flex;
  justify-content: flex-end;
}
.pd-btn-text {
  background: white;
  border: 1px solid #e2e8f0;
  color: #3b82f6;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.pd-bottom-spacer {
  height: 20px;
}

/* Footer */
.pd-footer {
  padding: 16px 24px 24px;
  background: white;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.pd-btn-book {
  width: 100%;
  height: 56px;
  border-radius: 100px;
  font-size: 16px;
  font-weight: 700;
  background: #3b82f6;
  border: none;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.25);
}
.pd-btn-book:hover, .pd-btn-book:focus {
  background: #2563eb;
}
</style>
<style>
/* Drawer Global Overrides */
.promo-detail-drawer .el-drawer {
  background-color: #f8fafc;
  max-width: 600px !important;
  margin: 0 auto !important;
  display: flex !important;
  flex-direction: column !important;
}
.promo-detail-drawer .el-drawer__body {
  padding: 0 !important;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  height: 100%;
}
</style>
